import React, { useEffect, useLayoutEffect } from 'react';
import { Linking, View } from 'react-native';
import { format } from 'date-fns';
import { AppView, Button, CardView, rem, Spacer } from 'design-system';
import { IconGoogleSheets } from 'design-system/icons';
import CardRowContainer from 'design-system/CardRowContainer';
import CardRow from 'design-system/CardRow';
import Text from 'design-system/Text';
import { useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { postLiveSheetDisable, } from './actions';
import { LiveExportStatus } from './LiveExportStatus';
import { useAuthorizeInGoogle } from './useAuthorizeInGoogle';
import { useLiveSheetUpdatedAtIntervalInitWithCache } from './useLiveSheet';
const strings = {
    spreadsheetName: 'Emma transactions',
    screenTitle: 'Live export',
    spreadsheetOpenLabel: 'Open',
    statusLabel: 'Status',
    lastSyncedLabel: 'Last synced',
    googleAccountLabel: 'Google account',
    deleteButtonLabel: 'Delete',
    reconnectButtonLabel: 'Reconnect',
    needAuthenticateInfo: 'Your Google connection has expired. Please reconnect to continue syncing.',
    otherIssueInfo: 'Something went wrong with your connection. Please reconnect to continue syncing.',
    syncingInfo: 'We’re syncing your data to the Google Sheet. This may take some time.',
};
const SpreadsheetRow = ({ spreadsheetId, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<TouchableBounceCard onDark withPadding noMarginBottom onPress={() => {
            Linking.openURL(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`);
        }}>
      <View style={styles.spreadsheetRow}>
        <View style={styles.iconContainer}>
          <IconGoogleSheets width={44} height={44} border/>
        </View>
        <View style={styles.contentContainer}>
          <Text Text-16>
            {/* This is hardcoded. We do not use the actual spreadsheet name. */}
            {strings.spreadsheetName}
          </Text>
          <Text Text-16 color={colors.text.brand}>
            {strings.spreadsheetOpenLabel}
          </Text>
        </View>
      </View>
    </TouchableBounceCard>);
};
/**
 * This screen is where users see their live export status and stop this feature.
 * This screen should be visible only if the user has enabled live export.
 */
const ExportDataLiveScreen = ({ navigation }) => {
    const styles = useStyles(styleSet);
    const [, , doingPostDisable, doPostDisable] = useFetchOld();
    const [liveSheetStatus] = useLiveSheetUpdatedAtIntervalInitWithCache();
    const syncInProgress = liveSheetStatus?.syncInProgress ?? true;
    const needAuthenticate = liveSheetStatus?.needAuthenticate ?? false;
    const googleAccountName = liveSheetStatus?.googleAccountName;
    const spreadsheetId = liveSheetStatus?.spreadsheetId;
    const issue = liveSheetStatus?.issue;
    const lastSyncMs = liveSheetStatus?.lastSyncMs;
    useEffect(() => {
        if (liveSheetStatus?.isEnabled === false) {
            // Normal flow should not reach here.
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.popTo('Feed');
            }
        }
    }, [liveSheetStatus]);
    // If the user has just started this feature, the back-end needs time to schedule the first sync.
    // Meanwhile, showing "working" to indicate the feature is working makes sense for the system
    // but causes confusion for the user. We consider it "syncing" on the UI.
    const showSyncInProgress = syncInProgress || (!issue && !needAuthenticate && !spreadsheetId);
    const { authorizeInGoogle, isAuthorizing } = useAuthorizeInGoogle();
    const infoTextRow = (() => {
        if (showSyncInProgress) {
            return <CardRow small title={strings.syncingInfo}/>;
        }
        if (issue) {
            const text = needAuthenticate
                ? strings.needAuthenticateInfo
                : strings.otherIssueInfo;
            return (<CardRow small title={<Text TextThin-14 Negative>
              {text}
            </Text>}/>);
        }
        return null;
    })();
    const buttonProps = {
        medium: true,
        flex: true,
        square: true,
        preventDoubleTap: true,
    };
    return (<AppView avoidHeader>
      {spreadsheetId !== undefined ? (<SpreadsheetRow spreadsheetId={spreadsheetId}/>) : null}
      <Spacer.H16 />
      <CardView noMarginTop>
        <CardRowContainer>
          <CardRow small tableSpacing title={strings.statusLabel} info={<LiveExportStatus active={!needAuthenticate && !issue} syncInProgress={showSyncInProgress}/>}/>
          <CardRow small tableSpacing title={strings.lastSyncedLabel} info={lastSyncMs ? format(new Date(lastSyncMs), 'd MMM h:mm a') : null}/>
          <CardRow small tableSpacing title={strings.googleAccountLabel} info={googleAccountName}/>
        </CardRowContainer>

        {infoTextRow}

        <View style={styles.buttonsContainer}>
          {issue ? (<>
              <Button errorReversed title={strings.reconnectButtonLabel} onPress={authorizeInGoogle} loading={isAuthorizing} {...buttonProps}/>
              <Spacer w={8}/>
            </>) : null}
          <Button brandBorder title={strings.deleteButtonLabel} onPress={async () => {
            const result = await doPostDisable(postLiveSheetDisable());
            if (result) {
                if (navigation.canGoBack()) {
                    navigation.goBack();
                }
                else {
                    navigation.navigate('Feed');
                }
            }
        }} loading={doingPostDisable} {...buttonProps}/>
        </View>
      </CardView>
    </AppView>);
};
const Wrapped = (props) => {
    const { navigation } = props;
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.screenTitle,
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    return <ExportDataLiveScreen {...props}/>;
};
const styleSet = createStyleSheets(() => ({
    buttonsContainer: {
        flexDirection: 'row',
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    spreadsheetRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconContainer: {
        alignItems: 'center',
        marginRight: rem(16),
        justifyContent: 'center',
        width: rem(44),
        height: rem(44),
    },
    contentContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));
export default Wrapped;
